// =============================================================
// SECTION SHELL — section-level layout constraints, safe-area,
// stacking context. Mimari planda: "owns section-level layout".
// =============================================================

import { type ReactNode, useRef, useEffect } from "react";
import { cn } from "@/lib/utils/cn";

export type SectionTone = "paper" | "paper-warm" | "ink" | "accent";

interface SectionShellProps {
  id: string;
  ariaLabelledBy?: string;
  tone?: SectionTone;
  container?: "narrow" | "default" | "wide";
  className?: string;
  children: ReactNode;
}

const TONE_BG: Record<SectionTone, string> = {
  "paper":      "var(--paper)",
  "paper-warm": "var(--paper-warm)",
  "ink":        "var(--ink)",
  "accent":     "var(--accent)",
};

const TONE_INK: Record<SectionTone, "dark" | "light"> = {
  "paper":      "dark",
  "paper-warm": "dark",
  "ink":        "light",
  "accent":     "light",
};

const CONTAINER: Record<NonNullable<SectionShellProps["container"]>, string> = {
  narrow:  "container--narrow",
  default: "container",
  wide:    "container--wide",
};

export function SectionShell({
  id,
  ariaLabelledBy,
  tone = "paper",
  container = "default",
  className,
  children,
}: SectionShellProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const isLight = TONE_INK[tone] === "light";

  // Tüm section roots `isolation: isolate` — sadece gerektiğinde (mimari plan)
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    el.style.isolation = "isolate";
    el.style.color = isLight ? "var(--paper)" : "var(--ink)";
  }, [isLight]);

  return (
    <section
      id={id}
      ref={sectionRef}
      aria-labelledby={ariaLabelledBy}
      className={cn("section-shell", `tone-${tone}`, className)}
      style={{ background: TONE_BG[tone] }}
    >
      <div className={cn(CONTAINER[container])}>{children}</div>
    </section>
  );
}